import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Link2, Copy, Check, Linkedin, Twitter, MessageCircle, Mail, ExternalLink, Sparkles, Rss } from 'lucide-react';
import { toast } from 'sonner';
import { resolveCountry, boardsForCountry, AUTOMATIC_BOARDS } from '../../lib/jobBoards';
import { formatSalary } from '../../lib/salary';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const chip = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.8rem', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', border: '1px solid #ECE7DE', background: '#fff', color: '#475569', transition: 'background-color 0.15s, border-color 0.15s' };
const sectionLabel = { fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#64748B', marginBottom: '0.5rem' };

export function DistributeDialog({ open, onOpenChange, job }) {
  const jobId = job?.id || job?._id;
  const publicUrl = `${window.location.origin}/careers/${jobId}`;
  const feedUrl = `${API_BASE}/api/public/jobs-feed.xml`;
  const sitemapUrl = `${API_BASE}/api/public/careers-sitemap.xml`;
  const country = useMemo(() => resolveCountry(job?.location), [job]);
  const boards = useMemo(() => boardsForCountry(country.code), [country]);
  const [selected, setSelected] = useState(() => new Set());
  const [copied, setCopied] = useState('');

  const allSelected = boards.length > 0 && selected.size === boards.length;
  const toggle = (name) => setSelected((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(boards.map((b) => b.name)));

  const jobText = [
    job?.title || '',
    formatSalary(job) ? `Salary: ${formatSalary(job)}` : '',
    job?.location ? `Location: ${job.location} · ${job?.job_type || ''}` : '',
    '',
    job?.description || '',
    (job?.required_skills || []).length ? `\nSkills: ${(job.required_skills || []).join(', ')}` : '',
    `\nApply: ${publicUrl}?src=board`,
  ].filter(Boolean).join('\n');

  const doCopy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); toast.success('Copied'); setTimeout(() => setCopied(''), 1500); }).catch(() => toast.error('Could not copy'));
  };

  const shareText = encodeURIComponent(`${job?.title || 'We are hiring'} — apply here:`);
  const tagged = (channel) => encodeURIComponent(`${publicUrl}?src=${channel}`);
  const shareLinks = [
    { name: 'LinkedIn', Icon: Linkedin, url: `https://www.linkedin.com/sharing/share-offsite/?url=${tagged('linkedin')}`, color: '#0A66C2' },
    { name: 'X', Icon: Twitter, url: `https://twitter.com/intent/tweet?text=${shareText}&url=${tagged('x')}`, color: '#002A38' },
    { name: 'WhatsApp', Icon: MessageCircle, url: `https://wa.me/?text=${shareText}%20${tagged('whatsapp')}`, color: '#25D366' },
    { name: 'Email', Icon: Mail, url: `mailto:?subject=${encodeURIComponent(job?.title || 'Job opening')}&body=${shareText}%20${tagged('email')}`, color: '#64748B' },
  ];

  const openSelected = () => {
    const chosen = boards.filter((b) => selected.has(b.name));
    if (chosen.length === 0) { toast.error('Select at least one board'); return; }
    navigator.clipboard.writeText(jobText).catch(() => {});
    chosen.forEach((b) => window.open(b.url, '_blank', 'noopener'));
    toast.success(`Opened ${chosen.length} board${chosen.length > 1 ? 's' : ''} · job text copied to paste`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 16, maxWidth: 620, maxHeight: '90vh', overflowY: 'auto' }} data-testid="distribute-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem' }}>Distribute this job</DialogTitle>
          <DialogDescription style={{ fontSize: '0.8125rem', color: '#64748B' }}>
            Share your public job page, post to free boards for <strong>{country.name}</strong>, and let Google/Bing index it automatically. Applications come straight back into CHIRE.
          </DialogDescription>
        </DialogHeader>

        {/* Public link */}
        <div style={{ marginTop: '0.5rem' }}>
          <div style={sectionLabel}>Public job page</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '0.55rem 0.75rem', borderRadius: 10, border: '1px solid #ECE7DE', background: '#FAF7F2', fontSize: '0.8125rem', color: '#002A38', overflow: 'hidden' }}>
              <Link2 size={14} color="#0076A3" style={{ flexShrink: 0 }} />
              <span data-testid="distribute-public-url" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{publicUrl}</span>
            </div>
            <button data-testid="copy-public-url" onClick={() => doCopy(publicUrl, 'url')} style={{ ...chip, background: '#0076A3', color: '#fff', borderColor: '#0076A3' }}>
              {copied === 'url' ? <Check size={14} /> : <Copy size={14} />} Copy link
            </button>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer" style={{ ...chip, textDecoration: 'none' }}><ExternalLink size={14} /> View</a>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {shareLinks.map((s) => (
              <a key={s.name} data-testid={`share-${s.name.toLowerCase()}`} href={s.url} target="_blank" rel="noopener noreferrer" style={{ ...chip, textDecoration: 'none', color: s.color, borderColor: '#ECE7DE' }}>
                <s.Icon size={14} /> {s.name}
              </a>
            ))}
            <button data-testid="copy-job-text" onClick={() => doCopy(jobText, 'text')} style={chip}>
              {copied === 'text' ? <Check size={14} /> : <Copy size={14} />} Copy job text
            </button>
          </div>
        </div>

        {/* Automatic */}
        <div style={{ marginTop: '1.25rem' }}>
          <div style={sectionLabel}>Automatic (no posting needed)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {AUTOMATIC_BOARDS.map((b) => (
              <div key={b.name} style={{ display: 'flex', gap: 10, padding: '0.6rem 0.75rem', borderRadius: 10, background: '#F0FDFA', border: '1px solid #99F6E4' }}>
                <Sparkles size={15} color="#0D9488" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#0F766E' }}>{b.name}</div>
                  <div style={{ fontSize: '0.6875rem', color: '#475569', lineHeight: 1.4 }}>{b.note}</div>
                </div>
                <a href={b.url} target="_blank" rel="noopener noreferrer" style={{ ...chip, textDecoration: 'none', alignSelf: 'center' }}><ExternalLink size={13} /> Open</a>
              </div>
            ))}
          </div>
        </div>

        {/* Geo boards */}
        <div style={{ marginTop: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <div style={{ ...sectionLabel, marginBottom: 0 }}>Free job boards · {country.name}</div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: '#334155', cursor: 'pointer' }}>
              <input data-testid="boards-select-all" type="checkbox" checked={allSelected} onChange={toggleAll} style={{ accentColor: '#0076A3', width: 15, height: 15 }} />
              Select all
            </label>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {boards.map((b) => (
              <label key={b.name} data-testid="board-row" style={{ display: 'flex', gap: 10, padding: '0.55rem 0.75rem', borderRadius: 10, background: selected.has(b.name) ? '#E5EFFF' : '#FAF7F2', border: `1px solid ${selected.has(b.name) ? '#BBE3F5' : '#EEF2F7'}`, cursor: 'pointer', alignItems: 'center', transition: 'background-color 0.15s' }}>
                <input type="checkbox" checked={selected.has(b.name)} onChange={() => toggle(b.name)} style={{ accentColor: '#0076A3', width: 15, height: 15 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#002A38' }}>{b.name}</div>
                  <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>{b.note}</div>
                </div>
                <a href={b.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ ...chip, textDecoration: 'none', padding: '0.3rem 0.6rem' }}><ExternalLink size={13} /></a>
              </label>
            ))}
          </div>
          <button data-testid="open-selected-boards" onClick={openSelected} style={{ marginTop: 10, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '0.65rem', borderRadius: 999, border: 'none', background: '#0076A3', color: '#fff', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }}>
            <ExternalLink size={15} /> Open selected boards & copy job text
          </button>
          <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: 6, lineHeight: 1.4 }}>
            Most boards need a quick account. We copy the job text so you can paste it, and every "Apply" link points back to your CHIRE page.
          </div>
        </div>

        {/* Feeds */}
        <div style={{ marginTop: '1.25rem' }}>
          <div style={sectionLabel}>Auto-pull feeds (for aggregators & Search Console)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[{ label: 'XML job feed', url: feedUrl, key: 'feed' }, { label: 'Careers sitemap', url: sitemapUrl, key: 'sitemap' }].map((f) => (
              <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0.75rem', borderRadius: 10, border: '1px solid #ECE7DE', background: '#FAF7F2' }}>
                <Rss size={14} color="#00A0E0" style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '0.75rem', color: '#002A38', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</span>
                <button data-testid={`copy-${f.key}`} onClick={() => doCopy(f.url, f.key)} style={{ ...chip, padding: '0.3rem 0.7rem' }}>
                  {copied === f.key ? <Check size={13} /> : <Copy size={13} />} Copy
                </button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
